import { Helmet } from "react-helmet-async";
import { toCleanUrl } from "@/lib/storageUrl";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  keywords?: string[];
  breadcrumbs?: BreadcrumbItem[];
  structuredData?: object | object[];
  imageAlt?: string;
}

const defaultTitle = "Nathan Stoker | Security Multimedia & Instructional Design Lead";
const defaultDescription =
  "Creating engaging eLearning experiences, custom SharePoint solutions, and compelling visual content that transforms complex concepts into memorable learning journeys.";
const siteUrl = "https://natestoker.com";

export function SEO({
  title,
  description = defaultDescription,
  image,
  url,
  type = "website",
  publishedTime,
  modifiedTime,
  author = "Nathan Stoker",
  keywords,
  breadcrumbs,
  structuredData,
  imageAlt,
}: SEOProps) {
  // Long titles get cut off in search results; only add the name when there's room.
  const pageTitle = title ? (title.length > 50 || title.includes("Nathan Stoker") ? title : `${title} | Nathan Stoker`) : defaultTitle;
  const pageUrl = url ? `${siteUrl}${url}` : siteUrl;
  // Transform Supabase storage URLs to clean branded URLs for better social sharing
  const rawImage = image || `${siteUrl}/og-image.webp`;
  const pageImage = toCleanUrl(rawImage);
  const pageImageAlt = imageAlt || title || "Nathan Stoker Portfolio";

  // Build structured data array
  const allStructuredData: object[] = [];

  // Add base Person schema on all pages
  allStructuredData.push({
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Nathan Stoker",
    url: siteUrl,
    jobTitle: "Security Multimedia & Instructional Design Lead",
    sameAs: [],
  });

  // Add breadcrumb schema if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    allStructuredData.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url.startsWith("http") ? item.url : `${siteUrl}${item.url}`,
      })),
    });
  }

  // Add custom structured data if provided
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      allStructuredData.push(...structuredData);
    } else {
      allStructuredData.push(structuredData);
    }
  }

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={pageUrl} />

      {/* Keywords */}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:alt" content={pageImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Nathan Stoker Portfolio" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:image:alt" content={pageImageAlt} />

      {/* Article specific */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}

      {/* RSS Feed */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title="Nathan Stoker Blog RSS Feed"
        href={`${siteUrl}/rss.xml`}
      />

      {/* Structured Data */}
      {allStructuredData.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(
            allStructuredData.length === 1
              ? allStructuredData[0]
              : allStructuredData
          )}
        </script>
      )}
    </Helmet>
  );
}
